// ============================================================================
// P4-CC41a — email address shaping for the SIGNUP SCREEN.
//
// ⛔ THIS MODULE IS FOR THE MESSAGE, NOT FOR THE SECURITY.
// The barrier is `trg_00_signup_email_policy`, a BEFORE INSERT trigger on
// auth.users (20261004120000_cc41_signup_integrity.sql). That is what actually
// stops a disposable address, because `POST /auth/v1/signup` is a public GoTrue
// endpoint and this file is not on the path to it. Everything here exists so
// that a person typing into our form is told something useful BEFORE a network
// round-trip, instead of watching a spinner and then reading a server error.
//
// ⚠️ THE TWO LISTS ARE ALLOWED TO DRIFT, AND THE DATABASE ONE IS AUTHORITATIVE.
// blocked_email_domains is designed to be extended by a plain INSERT with no
// code deploy — that is the whole point of putting it in a table. So the copy
// below WILL fall behind, by design. It holds only the handful anyone actually
// types. A domain missing from here is not a bug: the trigger still refuses it,
// one round-trip later, with its own sentence.
//
// ⛔ DO NOT "SYNC" THESE BY GENERATING THIS FILE FROM THE TABLE. That turns a
// deliberately-stale convenience into a build-time dependency on a database, and
// it would put the full blocklist in a public JS bundle for no gain.
//
// Pure: no imports, no network, no side effects. Nothing here throws.
// ============================================================================

// The ones people actually reach for, and the ones our own harness names. Kept
// deliberately short — see the drift note above.
//
// ⚠️ ADVISORY COPY, FOR INLINE UI MESSAGING ONLY. It is not a check anyone has
// to pass. public.blocked_email_domains is AUTHORITATIVE and is enforced by a
// BEFORE INSERT trigger on auth.users (trg_00_signup_email_policy), which is the
// only thing that actually refuses a signup.
// 📌 A DOMAIN HAND-PICKED INTO THE CC41a SEED MUST BE ADDED HERE TOO. They are
// kept in the same order so a diff between them is readable; this copy had
// already drifted four domains behind the seed before anyone noticed, because
// nothing fails when it does — the signup is still refused, just a round-trip
// later and with the database's wording instead of ours.
// ⛔ THE CC41c BULK IMPORT (10,123 domains from a public list, migration
// 20261103120000) IS DELIBERATELY NOT MIRRORED HERE. See the drift note at the
// top: this file is for the message, the table is the barrier, and the full
// list does not belong in a public JS bundle. jbsze.net is here only because
// it was seen on a real production signup — the same reason as onldm.net.
const COMMON_DISPOSABLE = [
  "10minutemail.com",
  "mailinator.com",
  "guerrillamail.com",
  "tempmail.com",
  "temp-mail.org",
  "yopmail.com",
  "throwawaymail.com",
  "sharklasers.com",
  "getnada.com",
  "dispostable.com",
  "trashmail.com",
  "fakeinbox.com",
  "maildrop.cc",
  "mintemail.com",
  "spamgourmet.com",
  "mohmal.com",
  "tempr.email",
  "emailondeck.com",
  "onldm.net",
  "jbsze.net",
];

// Mirrors allowed_email_domains. Checked FIRST and wins, exactly as the SQL
// does — if the two disagreed on precedence, the browser would refuse addresses
// the database happily accepts, which is the worst of both.
const ALLOWED = ["example.com"];

/**
 * Trim and lowercase. Nothing else.
 *
 * ⛔ DELIBERATELY DOES NOT STRIP PLUS-TAGS OR GMAIL DOTS.
 * jane+atlas@gmail.com is a legitimate address that real customers use on
 * purpose to filter their own mail, and jane.doe@gmail.com is simply somebody's
 * address. "Canonicalising" either one means we store, and mail, an address the
 * customer did not give us — and if it were used for uniqueness it would merge
 * two different people's accounts. Case and surrounding whitespace are the only
 * things a user can get "wrong" by accident; the rest is their address.
 *
 * @param {string} raw
 * @returns {string}
 */
export function normaliseEmail(raw) {
  return String(raw ?? "").trim().toLowerCase();
}

/**
 * The domain part, lowercased — or "" when there isn't one.
 *
 * "" is returned for null, "", "no-at-sign", "trailing@" and "@leading". The
 * caller gets one falsy answer for every shape of "there is no domain here",
 * because none of them is this function's problem to diagnose.
 *
 * @param {string} raw
 * @returns {string}
 */
export function emailDomain(raw) {
  const at = normaliseEmail(raw).lastIndexOf("@");
  if (at < 1) return "";           // no '@', or '@' first — nothing before it
  return normaliseEmail(raw).slice(at + 1);
}

/**
 * Every suffix of a domain, longest first:
 *   "mail.foo.co.uk" -> ["mail.foo.co.uk", "foo.co.uk", "co.uk", "uk"]
 *
 * This is what makes `mail.10minutemail.com` catchable by the
 * `10minutemail.com` entry, and it is the reason matching is NOT a substring
 * test: `notmailinator.com` has suffixes ["notmailinator.com", "com"], neither
 * of which is `mailinator.com`, so it is correctly left alone. Mirrors the
 * generated-suffix join in email_domain_blocked().
 *
 * @param {string} domain
 * @returns {string[]}
 */
export function domainSuffixes(domain) {
  const d = String(domain ?? "").trim().toLowerCase();
  if (!d) return [];
  const labels = d.split(".").filter(Boolean);
  if (labels.length === 0) return [];
  return labels.map((_, i) => labels.slice(i).join("."));
}

/**
 * Best-effort "this looks like a throwaway address" for the signup form.
 *
 * ⚠️ A `false` HERE MEANS NOTHING. It means only that this small list did not
 * recognise it — not that the address is acceptable. The trigger decides.
 *
 * @param {string} raw
 * @returns {boolean}
 */
export function isLikelyDisposable(raw) {
  const suffixes = domainSuffixes(emailDomain(raw));
  if (suffixes.length === 0) return false;
  if (suffixes.some((s) => ALLOWED.includes(s))) return false;
  return suffixes.some((s) => COMMON_DISPOSABLE.includes(s));
}
